import { useCallback, useEffect, useMemo, useState } from "react";
import { STORAGE_KEYS } from "../config/newsConfig";
import { readStoredJson, writeStoredJson } from "../utils/storage";
import { sortNewsByPublished } from "../utils/newsSort";

const CATEGORY_ALIASES = {
  all: "all",

  // Canonical internal category ids.
  politics: "politics",
  political: "politics",
  economy: "economy",
  economic: "economy",
  society: "society",
  social: "society",
  sports: "sports",
  sport: "sports",
  tech: "tech",
  technology: "tech",
  entertainment: "entertainment",
  health: "health",
  car: "car",
  auto: "car",
  automotive: "car",
  misc: "misc",
  other: "misc",

  // Final per-news taxonomy returned by the production API.
  "سیاسی": "politics",
  "اقتصادی": "economy",
  "جامعه": "society",
  "ورزشی": "sports",
  "تکنولوژی": "tech",
  "سرگرمی": "entertainment",
  "سلامت": "health",
  "خودرو": "car",
  "سایر": "misc",

  // Legacy aliases are retained only for already-saved local items.
  general: "misc",
  "عمومی": "misc",
  "اجتماعی": "society",
  "ورزش": "sports",
  "فناوری": "tech",
  "خودرویی": "car",
  "متفرقه": "misc",
};

const normalizeNewsCategory = (category) => {
  const key = String(category || "").trim().toLowerCase();
  return CATEGORY_ALIASES[key] || "misc";
};

const readSavedItems = () => {
  const stored = readStoredJson(STORAGE_KEYS.savedItems, {});
  return stored && typeof stored === "object" && !Array.isArray(stored)
    ? stored
    : {};
};

const getNewsLink = (newsOrLink) => {
  if (typeof newsOrLink === "string") return newsOrLink;
  return newsOrLink?.link || "";
};

export const useSavedNews = ({
  allNews = [],
  pendingNews = [],
  focusNewsBySource = {},
  categoryFilters = ["all"],
  activeFilter = "all",
} = {}) => {
  const [savedLinks, setSavedLinks] = useState(() => {
    return new Set(readStoredJson(STORAGE_KEYS.saved, []));
  });

  const [savedItemsByLink, setSavedItemsByLink] = useState(readSavedItems);

  useEffect(() => {
    writeStoredJson(STORAGE_KEYS.saved, [...savedLinks]);
  }, [savedLinks]);

  useEffect(() => {
    writeStoredJson(STORAGE_KEYS.savedItems, savedItemsByLink);
  }, [savedItemsByLink]);

  const toggleSavedNews = useCallback((newsOrLink) => {
    const link = getNewsLink(newsOrLink);
    if (!link) return;

    const news =
      typeof newsOrLink === "object" && newsOrLink !== null
        ? newsOrLink
        : null;

    setSavedLinks((prev) => {
      const next = new Set(prev);

      if (next.has(link)) {
        next.delete(link);

        setSavedItemsByLink((items) => {
          if (!items[link]) return items;
          const nextItems = { ...items };
          delete nextItems[link];
          return nextItems;
        });
      } else {
        next.add(link);

        if (news) {
          setSavedItemsByLink((items) => ({
            ...items,
            [link]: news,
          }));
        }
      }

      return next;
    });
  }, []);

  const clearSavedLinks = useCallback(() => {
    setSavedLinks(new Set());
    setSavedItemsByLink({});
  }, []);

  const liveNewsByLink = useMemo(() => {
    const combined = [
      ...allNews,
      ...pendingNews,
      ...Object.values(focusNewsBySource).flat(),
    ];

    return combined.reduce((result, item) => {
      if (item?.link) result[item.link] = item;
      return result;
    }, {});
  }, [allNews, focusNewsBySource, pendingNews]);

  useEffect(() => {
    setSavedItemsByLink((prev) => {
      let changed = false;
      const next = { ...prev };

      savedLinks.forEach((link) => {
        const liveItem = liveNewsByLink[link];
        if (!liveItem) return;

        if (JSON.stringify(prev[link]) !== JSON.stringify(liveItem)) {
          next[link] = liveItem;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [liveNewsByLink, savedLinks]);

  const savedNews = useMemo(() => {
    const unique = [];
    const seen = new Set();

    savedLinks.forEach((link) => {
      const item = liveNewsByLink[link] || savedItemsByLink[link];

      if (!item?.link || seen.has(item.link)) return;

      seen.add(item.link);
      unique.push(item);
    });

    return sortNewsByPublished(unique);
  }, [liveNewsByLink, savedItemsByLink, savedLinks]);

  const savedPages = useMemo(() => {
    return categoryFilters.reduce((pages, filter) => {
      pages[filter] =
        filter === "all"
          ? savedNews
          : savedNews.filter(
              (item) => normalizeNewsCategory(item?.category) === filter
            );

      return pages;
    }, {});
  }, [categoryFilters, savedNews]);

  const activeSavedItems = savedPages[activeFilter] || [];

  return {
    savedLinks,
    savedNews,
    savedPages,
    activeSavedItems,
    toggleSavedNews,
    clearSavedLinks,
  };
};
